/**
 * Shared stateless validation for component drop/placement rules.
 * Used by both MCP component-tools.ts (validateDrop) and
 * drag-drop-helper.ts (getElementIsDroppable, CREATE path).
 *
 * NOTE: Placement/wrapping logic is NOT shared here because MCP and
 * drag-drop use different async/sync patterns. See:
 * - MCP: component-tools.ts → addWithRowColWrap()
 * - Drag-drop: drag-drop-helper.ts → wrapDIWithDefaultRow()
 */

import { ComponentType } from "@kuika/kuika-cl-model";
import {
  HSTACK_ALLOWED_SET,
  MENU_TARGETS,
  MENU_CHILDREN,
  TABLE_COLUMN_CONTAINERS,
  TABLE_TARGETS,
  COLUMN_COMPONENTS,
  AUTO_COLUMNS,
  STATICTABLE_STRUCTURAL_CHILDREN
} from "./component-drop-constants";

export interface ComponentDropValidationContext {
  /** Name of the target container component (e.g. "HorizontalStack", "Row"). */
  targetName: string;
  /** Name of the component being added (e.g. "Label", "Panel"). */
  componentName: string;
  /** Component definition of the target, if available. */
  targetComponentDef?: { componentType: number; isDataRepeater?: boolean; name?: string };
  /** Component definition of the source/added component, if available. */
  sourceComponentDef?: { componentType: number; isDataRepeater?: boolean; name?: string };
  /** Whether the target has children interaction disabled. */
  isTargetInteractionDisabled: boolean;
  /** Whether the target is inside a Form. */
  isTargetInsideForm: boolean;
  /** Whether the screen is in absolute design mode. */
  isAbsoluteDesign: boolean;
}

/**
 * Validates whether a component can be placed into a target container.
 * Returns an error message string if the drop is invalid, or null if valid.
 *
 * This is a pure function with no side effects — no notifications, no DOM access.
 */
export function validateComponentDrop(ctx: ComponentDropValidationContext): string | null {
  const { targetName, componentName } = ctx;

  // Rule 1: No components into Form
  if (targetName === "Form" || ctx.isTargetInsideForm) {
    return "Cannot add components inside a Form.";
  }

  // Rule 2: No Row/Col into Stacks
  if (
    (targetName === "HorizontalStack" || targetName === "VerticalStack" || targetName === "Spacer") &&
    (componentName === "Row" || componentName === "Col")
  ) {
    return "Cannot add Row or Col into Stacks. Use VerticalStack or HorizontalStack directly.";
  }

  // Rule 3: Only Col allowed directly in Row
  if (targetName === "Row" && componentName !== "Col" && componentName !== "Row") {
    return "Only Col components can be added directly into a Row. Add your component into a Col instead.";
  }

  // Rule 4: HorizontalStack whitelist
  if (targetName === "HorizontalStack" && !HSTACK_ALLOWED_SET.has(componentName.toLowerCase())) {
    return `Cannot add ${componentName} into HorizontalStack. Only simple components are allowed.`;
  }

  // Rule 5: Menu child restrictions
  if (MENU_TARGETS.has(targetName) && !MENU_CHILDREN.has(componentName)) {
    return `Cannot add ${componentName} into ${targetName}. Only MenuItem and SubMenu are allowed.`;
  }

  // Rule 6: No data repeater into data repeater
  // Exception: TableDefaultColumn/TableTemplateColumn into TableRow is a legitimate hierarchy
  if (
    ctx.sourceComponentDef?.isDataRepeater &&
    ctx.targetComponentDef?.isDataRepeater &&
    !((componentName === "TableDefaultColumn" || componentName === "TableTemplateColumn") && targetName === "TableRow")
  ) {
    return "Cannot nest data repeaters inside data repeaters.";
  }

  // Rule 6b: StaticTable-family structural parents accept only their band/cell children.
  // StaticTable → StaticTableRow/StaticTableHeader; StaticTableRow/StaticTableHeader → StaticTableColumn.
  // Without this, an add (MCP/markup, or a manual before/after drop) lands a bare element directly
  // under a StaticTableRow with no StaticTableColumn wrapper — the KOA-18016 corruption that makes the
  // table's columns unselectable and blocks further drops. StaticTableColumn hosts arbitrary cell
  // content and is intentionally not gated. Scoped to the StaticTable family, so the KOA-16241 markup
  // carve-out (TABLE_COLUMN_CONTAINERS below) and the KOA-17797 Menu rules are untouched.
  const staticTableAllowedChildren = STATICTABLE_STRUCTURAL_CHILDREN[targetName];
  if (staticTableAllowedChildren && !staticTableAllowedChildren.has(componentName)) {
    return `Cannot add ${componentName} directly into ${targetName}. Place it inside a StaticTableColumn cell instead.`;
  }

  // Rule 7: Target must be a container (simple = not a container)
  // Exceptions: (a) certain table column types are marked simple but accept
  // children (TABLE_COLUMN_CONTAINERS); (b) structural nav parents — Menu is
  // componentType:simple in Components.json yet legitimately owns its declared
  // MenuItem/SubMenu children. Rule 5 above has already confirmed a
  // MENU_TARGETS→MENU_CHILDREN pair is valid, so the generic "not a container"
  // rejection must not fire for it. Without this carve-out the markup
  // translator's <Menu><MenuItem> adds (and manual MenuItem drags) are dropped
  // here — the documented, previously-reverted Menu regression (KOA-17797).
  if (
    ctx.targetComponentDef &&
    ctx.targetComponentDef.componentType === ComponentType.simple &&
    !TABLE_COLUMN_CONTAINERS.has(targetName) &&
    !(MENU_TARGETS.has(targetName) && MENU_CHILDREN.has(componentName))
  ) {
    return `Cannot add components into ${targetName} — it is not a container component.`;
  }

  // Rule 8: Table targets only accept column components
  if (TABLE_TARGETS.has(targetName) && !COLUMN_COMPONENTS.has(componentName)) {
    return `Cannot add ${componentName} directly into ${targetName}.`;
  }

  // Rule 9: EditableTableColumn/TableDefaultColumn don't accept arbitrary children
  if (AUTO_COLUMNS.has(targetName) && !COLUMN_COMPONENTS.has(componentName)) {
    return `Cannot add ${componentName} into ${targetName}. This column type does not accept child components.`;
  }

  // Rule 10: No Panel into Panel in absolute design
  if (ctx.isAbsoluteDesign && targetName === "Panel" && componentName === "Panel") {
    return "Cannot add Panel into Panel in absolute design mode.";
  }

  // Rule 11: Interaction-disabled check
  if (ctx.isTargetInteractionDisabled) {
    return `Cannot add components into ${targetName} — interaction is disabled.`;
  }

  return null; // valid
}
